import { Calendar, ChevronUp, RotateCcw } from "feather-icons-react/build/IconComponents";
import React, { useState, useEffect, useMemo } from "react";
import CountUp from "react-countup";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import "bootstrap-daterangepicker/daterangepicker.css";
import DateRangePicker from "react-bootstrap-daterangepicker";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Link } from "react-router-dom";
import { setToogleHeader } from "../../core/redux/action";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "react-bootstrap";
import Table from "../../core/pagination/datatable";
import useProducts from "../../hooks/useProducts";
import Select from "react-select";
import { Filter } from "feather-icons-react/build/IconComponents";

const SalesDashboard = () => {
  // Récupération des données via le hook avec des valeurs par défaut
  const { products = [], categoryData = [], stockMoves = [] } = useProducts();

  /** États pour les filtres de la liste **/
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);
  const [selectedOperationFilter, setSelectedOperationFilter] = useState(null);

  /** États pour le formulaire de création **/
  const [selectedStore] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  /** Options statiques pour les stocks **/
  const storeOptions = [
    { value: "Bar", label: "Bar" },
    { value: "Exposition", label: "Exposition" },
    { value: "Cuisine", label: "Cuisine" },
    { value: "Stock général", label: "Stock général" },
  ];

  /** Préparation des options pour les catégories **/
  const categoryOptions = categoryData.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  /** Filtrage des produits en fonction du stock sélectionné **/
  const filteredProducts = selectedStore
    ? products.filter((product) => product.stock === selectedStore.value)
    : products;

  /** Options pour l’opération (pour le formulaire et le filtre) **/
  const moveOptions = [
    { value: "vente", label: "Vente" },
    { value: "entrée", label: "Entrée" },
    { value: "sortie", label: "Sortie" },
  ];

  /** Vérification que le produit sélectionné appartient au stock choisi **/
  useEffect(() => {
    if (selectedStore && selectedProduct) {
      const stillValid = filteredProducts.some(
        (prod) => prod.id === selectedProduct.value
      );
      if (!stillValid) {
        setSelectedProduct(null);
      }
    }
  }, [selectedStore, selectedProduct, filteredProducts]);

  /**************** Gestion de la plage de dates ****************/
  // Date d'aujourd'hui et 7 jours en arrière comme valeur par défaut
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  // Stocker la plage de dates sélectionnée dans le state
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: sevenDaysAgo,
    endDate: today,
  });

  // Paramètres initiaux pour le DateRangePicker avec des dates réelles
  const initialSettings = {
    startDate: selectedDateRange.startDate,
    endDate: selectedDateRange.endDate,
    timePicker: false,
    ranges: {
      "Aujourd'hui": [today, today],
      "7 derniers jours": [sevenDaysAgo, today],
      "30 derniers jours": [
        new Date(new Date().setDate(today.getDate() - 30)),
        today,
      ],
      "Ce mois-ci": [
        new Date(today.getFullYear(), today.getMonth(), 1),
        new Date(today.getFullYear(), today.getMonth() + 1, 0),
      ],
    },
  };

  // Callback déclenché lors de la sélection d'une nouvelle plage
  const handleDateRangeApply = (event, picker) => {
    setSelectedDateRange({
      startDate: picker.startDate.toDate(),
      endDate: picker.endDate.toDate(),
    });
  };

  // Filtrer les stockMoves en fonction de la plage de dates sélectionnée
  const filteredStockMovesByDate = useMemo(() => {
    return (stockMoves || []).filter((move) => {
      const moveDate = new Date(move.created_at);
      return (
        moveDate >= selectedDateRange.startDate &&
        moveDate <= selectedDateRange.endDate
      );
    });
  }, [stockMoves, selectedDateRange]);

  /**************** Filtrage combiné des mouvements ****************/
  const filteroperations = useMemo(() => {
    return (filteredStockMovesByDate || []).filter((stockMove) => {
      const productName = stockMove.product?.name || "";
      const matchesName = searchTerm
        ? productName.toLowerCase().includes(searchTerm.toLowerCase())
        : true;
      const matchesOperation = selectedOperationFilter
        ? stockMove.move_operation === selectedOperationFilter.value
        : true;
      const matchesStock = selectedStock
        ? stockMove.product?.stock === selectedStock.value
        : true;
      const matchesCategory = selectedCategory
        ? stockMove.product?.category_id === selectedCategory.value
        : true;
      return matchesName && matchesOperation && matchesStock && matchesCategory;
    });
  }, [filteredStockMovesByDate, searchTerm, selectedOperationFilter, selectedStock, selectedCategory]);

  /** Toggle de la visibilité des filtres **/
  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevVisibility) => !prevVisibility);
  };

  /** Définition des colonnes du tableau **/
  const columns = [
    {
      title: "Effectué par",
      dataIndex: "user",
      render: (user) => user?.name || "Non défini",
    },
    {
      title: "Produit",
      dataIndex: "product",
      render: (product) => product?.name || "Non défini",
    },
    {
      title: "Stock",
      dataIndex: "product",
      render: (product) => product?.stock || "Non défini",
    },
    {
      title: "Prix vente",
      dataIndex: "product",
      render: (product) => product?.variations?.[0]?.prix_vente || "Non défini",
    },
    {
      title: "Prix achat",
      dataIndex: "product",
      render: (product) => product?.variations?.[0]?.prix_achat || "Non défini",
    },
    {
      title: "Quantité",
      dataIndex: "quantity_move",
      key: "quantity_move",
    },
    {
      title: "Mouvement",
      dataIndex: "move_operation",
      key: "move_operation",
    },
    {
      title: "Date",
      dataIndex: "created_at",
      render: (text, record) => {
        const date = new Date(record.created_at);
        return date.toLocaleDateString("fr-FR", {
          minute: "numeric",
          hour: "numeric",
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      },
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
  ];

  const dispatch = useDispatch();
  const toggleHeader = useSelector((state) => state.toggle_header);
  const user = useSelector((state) => state.auth.user);

  // Ces fonctions gèrent les tooltips "Refresh" et "Collapse"
  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );
  const renderCollapseTooltip = (props) => (
    <Tooltip id="collapse-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  /**************** Calcul des ventes et bénéfices ****************/
  // Filtrer les mouvements (par date) de type "vente"
  const saleMoves = useMemo(() => {
    return filteredStockMovesByDate.filter(
      (move) => move.move_operation === "vente"
    );
  }, [filteredStockMovesByDate]);

  // Total des ventes : quantité vendue x prix de vente
  const totalSales = useMemo(() => {
    return saleMoves.reduce((acc, move) => {
      const salePrice = move.product?.variations?.[0]?.prix_vente || 0;
      return acc + move.quantity_move * salePrice;
    }, 0);
  }, [saleMoves]);

  // Coût total d'achat : quantité vendue x prix d'achat
  const totalPurchase = useMemo(() => {
    return saleMoves.reduce((acc, move) => {
      const purchasePrice = move.product?.variations?.[0]?.prix_achat || 0;
      return acc + move.quantity_move * purchasePrice;
    }, 0);
  }, [saleMoves]);

  // Bénéfice = total des ventes - coût total d'achat
  const totalProfit = totalSales - totalPurchase;

  /**************** Fin des calculs ****************/

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="welcome d-lg-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center welcome-text">
              <h3 className="d-flex align-items-center">
                <ImageWithBasePath src="assets/img/icons/hi.svg" alt="img" />
                &nbsp;Salut {user.name},
              </h3>
              &nbsp;
              <h6>voici ce qui se passe chez nous aujourd&apos;hui.</h6>
            </div>
            <div className="d-flex align-items-center">
              <div className="position-relative daterange-wraper me-2">
                <div className="input-groupicon calender-input">
                  <DateRangePicker
                    initialSettings={initialSettings}
                    onApply={handleDateRangeApply}
                  >
                    <input
                      className="form-control col-4 input-range"
                      type="text"
                    />
                  </DateRangePicker>
                </div>
                <Calendar className="feather-14" />
              </div>
              <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                <Link data-bs-toggle="tooltip" data-bs-placement="top">
                  <RotateCcw className="feather feather-rotate-ccw feather-16" />
                </Link>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  id="collapse-header"
                  className={toggleHeader ? "active" : ""}
                  onClick={() => {
                    dispatch(setToogleHeader(!toggleHeader));
                  }}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </div>
          </div>

          {/* Cartes récapitulatives */}
          <div className="row sales-cards">
            {/* Carte Total produits */}
            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card color-info bg-secondary mb-4">
                <ImageWithBasePath
                  src="assets/img/icons/purchased-earnings.svg"
                  alt="img"
                />
                <h3>
                  <CountUp end={products.length} duration={4}>+</CountUp>
                </h3>
                <p>Total produits</p>
                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                  <Link data-bs-toggle="tooltip" data-bs-placement="top" className="feather-dashboard">
                    <RotateCcw className="feather-16" />
                  </Link>
                </OverlayTrigger>
              </div>
            </div>

            {/* Carte Ventes totale */}
            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card color-info bg-primary mb-4">
                <ImageWithBasePath
                  src="assets/img/icons/total-sales.svg"
                  alt="img"
                />
                <h3>
                  <CountUp end={totalSales} duration={4}>+</CountUp>XOF
                </h3>
                <p>Ventes totale</p>
                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                  <Link data-bs-toggle="tooltip" className="feather-dashboard">
                    <RotateCcw className="feather-16" />
                  </Link>
                </OverlayTrigger>
              </div>
            </div>

            {/* Carte Bénéfice sur vente */}
            <div className="col-xl-6 col-sm-12 col-12">
              <div className="card d-flex align-items-center justify-content-between default-cover mb-4">
                <div>
                  <h6>Bénéfice sur vente</h6>
                  <h3>
                    <span className="counters" data-count={totalProfit}>
                      {totalProfit.toFixed(2)}
                    </span>XOF
                  </h3>
                  <p className="sales-range">
                    <span className="text-success">
                      <ChevronUp className="feather-16" />
                      {/* Vous pouvez ajouter ici le pourcentage d'augmentation */}
                    </span>
                    augmentation par rapport à la semaine dernière
                  </p>
                </div>
                <ImageWithBasePath
                  src="assets/img/icons/weekly-earning.svg"
                  alt="img"
                />
              </div>
            </div>
          </div>

          {/* Section du tableau affichant les mouvements */}
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Derniers produits ajoutés</h4>
            </div>
            <div className="card table-list-card">
              <div className="card-body">
                <div className="table-top">
                  <div className="search-set">
                    <div className="search-input">
                      <input
                        type="text"
                        placeholder="Rechercher un produit..."
                        className="form-control form-control-sm formsearch"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="search-path">
                    <button className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}>
                      <Filter className="filter-icon" onClick={toggleFilterVisibility} />
                      <span onClick={toggleFilterVisibility}>
                        <ImageWithBasePath src="assets/img/icons/closes.svg" alt="img" />
                      </span>
                    </button>
                  </div>
                </div>

                {isFilterVisible && (
                  <div className="card filter-card">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-4">
                          <label>Catégorie</label>
                          <Select
                            options={categoryOptions}
                            value={selectedCategory}
                            onChange={setSelectedCategory}
                            placeholder="Choisir une catégorie"
                          />
                        </div>
                        <div className="col-lg-4">
                          <label>Stock</label>
                          <Select
                            options={storeOptions}
                            value={selectedStock}
                            onChange={setSelectedStock}
                            placeholder="Choisir un stock"
                          />
                        </div>
                        <div className="col-lg-4">
                          <label>Opération</label>
                          <Select
                            options={moveOptions}
                            value={selectedOperationFilter}
                            onChange={setSelectedOperationFilter}
                            placeholder="Choisir une opération"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="table-responsive">
                  <Table columns={columns} dataSource={filteroperations} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SalesDashboard;
